import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  IconButton,
  Pagination,
  Stack,
  Typography,
} from '@mui/material'
import { X } from 'react-feather'
import { Loader } from '@/components'
import {
  ContractorsMultiSelect,
  EmployeesMultiSelect,
  ManualHoursReportStatusSelect,
  SearchDateField,
} from '@/components/inputs'
import { EmptyListData } from '@/components/ui'
import {
  ManualHoursReportsSearchParams,
  useAuth,
  useFilters,
  useManualHoursReports,
  useModal,
} from '@/hooks'
import {
  ManualHoursReport,
  ManualHoursReportingTable,
} from '@/features/manualHoursReporting'
import { DEFAULT_LIST_LIMIT, DEFAULT_LIST_OFFSET } from '@/constants'
import {
  ManualHoursReport as ManualHoursReportType,
  PaginatedListResponse,
  UserGroups,
} from '@/types'
import { theme } from '@/theme'

export const ManualHoursReportingContainer = (): JSX.Element => {
  const { t } = useTranslation('manualHoursReporting')
  const { open } = useModal()

  const { hasPermission } = useAuth()
  const isHr = hasPermission([UserGroups.HR])
  const isEmployee = hasPermission([
    UserGroups.EMPLOYEE,
    UserGroups.MANAGER,
    UserGroups.SOUND_TECHNICIAN,
    UserGroups.DATA_CONTROLLER,
  ])

  const defaultFiltersParams: ManualHoursReportsSearchParams = {
    user: undefined,
    contractors: undefined,
    startDate: undefined,
    endDate: undefined,
    status: undefined,
    limit: DEFAULT_LIST_LIMIT,
    offset: DEFAULT_LIST_OFFSET,
  }

  const openManualHoursReport = (id: number) => {
    if (data && data.results) {
      const index = data.results.findIndex((row) => row.id === id)

      if (index !== -1) {
        open(<ManualHoursReport reportData={data.results[index]} />)
      }
    }
  }

  const {
    filterParams,
    handleFieldValueChange,
    handleResetFilters,
    initPagesCount,
    pagesCount,
    page,
    onPageChange,
    openDetail,
  } = useFilters<
    ManualHoursReportsSearchParams,
    PaginatedListResponse<ManualHoursReportType>,
    ManualHoursReportType
  >(defaultFiltersParams)

  const { data, isLoading } = useManualHoursReports(filterParams, true, isHr)

  useEffect(() => {
    if (data && data.count) {
      initPagesCount(data?.count)

      const detailItemId = openDetail()

      if (detailItemId) {
        openManualHoursReport(detailItemId)
      }
    }
  }, [data])

  const onCreate = () => {
    open(<ManualHoursReport />)
  }

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={4}
      >
        <Stack direction="column" justifyContent="flex-start" spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <SearchDateField
              searchStartValue={filterParams.startDate as string}
              searchEndValue={filterParams.endDate as string}
              onChangeStartDate={(date) =>
                handleFieldValueChange(date, 'startDate')
              }
              onChangeEndDate={(date) =>
                handleFieldValueChange(date, 'endDate')
              }
            />
          </Stack>

          {isHr && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <EmployeesMultiSelect
                value={filterParams.user}
                onChange={(value) => handleFieldValueChange(value, 'user')}
                placeholder={t('placeholders.employees')}
                groups={[
                  UserGroups.MANAGER,
                  UserGroups.EMPLOYEE,
                  UserGroups.SOUND_TECHNICIAN,
                  UserGroups.DATA_CONTROLLER,
                ]}
              />
              <ContractorsMultiSelect
                value={filterParams.contractors as string}
                onChange={(value) =>
                  handleFieldValueChange(value, 'contractors')
                }
                placeholder={t('placeholders.contractors')}
              />
              <ManualHoursReportStatusSelect
                value={filterParams.status as string}
                onChange={(value) => handleFieldValueChange(value, 'status')}
              />
            </Stack>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography color="green.600">
            {t('amount', { amount: data?.count ?? 0 })}
          </Typography>
          <IconButton
            onClick={handleResetFilters}
            title={t('buttons.resetFilters')}
          >
            <X size="16px" color={theme.palette.green['500']} />
          </IconButton>
          {isEmployee && (
            <Button variant="contained" color="primary" onClick={onCreate}>
              {t('buttons.addManualHours')}
            </Button>
          )}
        </Stack>
      </Stack>

      {data?.count ? (
        <>
          <ManualHoursReportingTable
            rows={data.results}
            onOpenDetails={openManualHoursReport}
            showUserColumn={isHr}
            showExportedColumn={isHr}
          />

          {pagesCount > 1 && (
            <Box alignSelf="center" mt={16}>
              <Pagination
                count={pagesCount}
                page={page}
                onChange={onPageChange}
              />
            </Box>
          )}
        </>
      ) : (
        <Box my={20}>
          {isLoading && <Loader />}

          {!isLoading && !data?.count && (
            <EmptyListData textKey="hoursReporting:noHoursReports" />
          )}
        </Box>
      )}
    </Stack>
  )
}
